import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UserProfile, Education, WorkExperience, Certification, SocialLinks, Reference, PaymentPreferences } from '../types';
import { authService } from '../services/api';
import { ArrowLeft, Save, User, Briefcase, DollarSign, Globe, FileText, Plus, X, Phone, Languages, GraduationCap, Building2, Award, Link as LinkIcon, Users, CreditCard } from 'lucide-react';

const EditProfile: React.FC = () => {
  const { user, userProfile, updateProfile, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const totalSteps = 5;

  // Step navigation functions
  const nextStep = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (step: number) => {
    setCurrentStep(step);
  };

  // Available options for form fields
  const availableSkills = [
    'AI Development', 'Machine Learning', 'Generative AI', 'Data Analytics', 'Data Visualization',
    'Data Engineering', 'Digital Marketing', 'SEO', 'Social Media Marketing', 'Graphic Design',
    'UI/UX Design', 'Video Editing', 'Web Development', 'Mobile App Development', 'Copywriting',
    'Content Writing', 'Translation', 'Voice Over', '3D Modeling', 'Animation', 'Game Development',
    'DevOps', 'Cloud Computing', 'Cybersecurity', 'Blockchain', 'IoT Development'
  ];

  const availableLanguages = [
    'English', 'Spanish', 'French', 'German', 'Chinese', 'Japanese', 'Korean', 'Arabic', 'Hindi', 'Portuguese'
  ];

  const availableIndustries = [
    'Technology', 'Healthcare', 'Finance', 'Education', 'Entertainment', 'E-commerce', 'Real Estate',
    'Manufacturing', 'Transportation', 'Energy', 'Media', 'Consulting', 'Non-profit', 'Government'
  ];

  const availableTools = [
    'React', 'Vue.js', 'Angular', 'Node.js', 'Python', 'Java', 'C++', 'C#', 'PHP', 'Ruby',
    'Docker', 'Kubernetes', 'AWS', 'Azure', 'Google Cloud', 'Figma', 'Adobe Creative Suite',
    'Blender', 'Unity', 'Unreal Engine', 'Tableau', 'Power BI', 'TensorFlow', 'PyTorch'
  ];

  const availableSoftSkills = [
    'Communication', 'Leadership', 'Problem Solving', 'Time Management', 'Teamwork', 'Adaptability',
    'Creativity', 'Critical Thinking', 'Emotional Intelligence', 'Conflict Resolution', 'Negotiation'
  ];
  const [formData, setFormData] = useState<UserProfile>({
    email: user?.email || '',
    name: user?.name || '',
    userType: user?.userType || 'freelancer',
    skills: [],
    experience_years: 0,
    cost_per_project: 0,
    bio: '',
    portfolio_url: '',
    availability: true,
    phone: '',
    languages: [],
    education: [],
    work_experience: [],
    certifications: [],
    social_links: {
      linkedin: '',
      github: '',
      twitter: '',
      website: '',
      dribbble: '',
      behance: ''
    },
    preferred_industry: [],
    tools_technologies: [],
    soft_skills: [],
    achievements: [],
    references: [],

    payment_preferences: {
      preferred_currencies: ['USD'],
      payment_methods: ['PayPal'],
      tax_id: ''
    }
  });

  useEffect(() => {
    if (userProfile) {
      setFormData({
        ...userProfile,
        email: userProfile.email || user?.email || '',
        name: userProfile.name || user?.name || '',
        userType: userProfile.userType || user?.userType || 'freelancer',
      });
    }
  }, [userProfile, user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: parseFloat(value) || 0,
    }));
  };

  const handleSkillsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const skills = e.target.value.split(',').map(skill => skill.trim()).filter(Boolean);
    setFormData(prev => ({
      ...prev,
      skills,
    }));
  };

  // Helper functions for managing dynamic arrays
  const addEducation = () => {
    const newEducation: Education = {
      institution: '',
      degree: '',
      field_of_study: '',
      start_date: '',
      end_date: '',
      description: '',
      gpa: undefined
    };
    setFormData(prev => ({
      ...prev,
      education: [...(prev.education || []), newEducation]
    }));
  };

  const updateEducation = (index: number, field: keyof Education, value: any) => {
    setFormData(prev => ({
      ...prev,
      education: (prev.education || []).map((edu, i) => 
        i === index ? { ...edu, [field]: value } : edu
      )
    }));
  };

  const removeEducation = (index: number) => {
    setFormData(prev => ({
      ...prev,
      education: (prev.education || []).filter((_, i) => i !== index)
    }));
  };

  const addWorkExperience = () => {
    const newExperience: WorkExperience = {
      company: '',
      position: '',
      start_date: '',
      end_date: '',
      current: false,
      description: '',
      achievements: [],
      technologies_used: []
    };
    setFormData(prev => ({
      ...prev,
      work_experience: [...(prev.work_experience || []), newExperience]
    }));
  };

  const updateWorkExperience = (index: number, field: keyof WorkExperience, value: any) => {
    setFormData(prev => ({
      ...prev,
      work_experience: (prev.work_experience || []).map((exp, i) => 
        i === index ? { ...exp, [field]: value } : exp
      )
    }));
  };

  const removeWorkExperience = (index: number) => {
    setFormData(prev => ({
      ...prev,
      work_experience: (prev.work_experience || []).filter((_, i) => i !== index)
    }));
  };

  const addCertification = () => {
    const newCert: Certification = {
      name: '',
      issuing_organization: '',
      issue_date: '',
      expiry_date: '',
      credential_id: '',
      credential_url: ''
    };
    setFormData(prev => ({
      ...prev,
      certifications: [...(prev.certifications || []), newCert]
    }));
  };

  const updateCertification = (index: number, field: keyof Certification, value: any) => {
    setFormData(prev => ({
      ...prev,
      certifications: (prev.certifications || []).map((cert, i) => 
        i === index ? { ...cert, [field]: value } : cert
      )
    }));
  };

  const removeCertification = (index: number) => {
    setFormData(prev => ({
      ...prev,
      certifications: (prev.certifications || []).filter((_, i) => i !== index)
    }));
  };

  const addReference = () => {
    const newRef: Reference = {
      name: '',
      position: '',
      company: '',
      email: '',
      phone: '',
      relationship: ''
    };
    setFormData(prev => ({
      ...prev,
      references: [...(prev.references || []), newRef]
    }));
  };

  const updateReference = (index: number, field: keyof Reference, value: any) => {
    setFormData(prev => ({
      ...prev,
      references: (prev.references || []).map((ref, i) => 
        i === index ? { ...ref, [field]: value } : ref
      )
    }));
  };

  const removeReference = (index: number) => {
    setFormData(prev => ({
      ...prev,
      references: (prev.references || []).filter((_, i) => i !== index)
    }));
  };

  const addAchievement = () => {
    setFormData(prev => ({
      ...prev,
      achievements: [...(prev.achievements || []), '']
    }));
  };

  const updateAchievement = (index: number, value: string) => {
    setFormData(prev => ({
      ...prev,
      achievements: (prev.achievements || []).map((ach, i) => 
        i === index ? value : ach
      )
    }));
  };

  const removeAchievement = (index: number) => {
    setFormData(prev => ({
      ...prev,
      achievements: (prev.achievements || []).filter((_, i) => i !== index)
    }));
  };

  // Toggle functions for multi-select fields
  const handleSkillToggle = (skill: string) => {
    setFormData(prev => {
      const currentSkills = prev.skills || [];
      const isSelected = currentSkills.includes(skill);
      if (isSelected) {
        return {
          ...prev,
          skills: currentSkills.filter(s => s !== skill)
        };
      } else {
        if (currentSkills.length >= 15) return prev;
        return {
          ...prev,
          skills: [...currentSkills, skill]
        };
      }
    });
  };

  const handleLanguageToggle = (language: string) => {
    setFormData(prev => {
      const currentLanguages = prev.languages || [];
      const isSelected = currentLanguages.includes(language);
      if (isSelected) {
        return {
          ...prev,
          languages: currentLanguages.filter(l => l !== language)
        };
      } else {
        return {
          ...prev,
          languages: [...currentLanguages, language]
        };
      }
    });
  };

  const handleIndustryToggle = (industry: string) => {
    setFormData(prev => {
      const currentIndustries = prev.preferred_industry || [];
      const isSelected = currentIndustries.includes(industry);
      if (isSelected) {
        return {
          ...prev,
          preferred_industry: currentIndustries.filter(i => i !== industry)
        };
      } else {
        return {
          ...prev,
          preferred_industry: [...currentIndustries, industry]
        };
      }
    });
  };

  const handleToolToggle = (tool: string) => {
    setFormData(prev => {
      const currentTools = prev.tools_technologies || [];
      const isSelected = currentTools.includes(tool);
      if (isSelected) {
        return {
          ...prev,
          tools_technologies: currentTools.filter(t => t !== tool)
        };
      } else {
        return {
          ...prev,
          tools_technologies: [...currentTools, tool]
        };
      }
    });
  };

  const handleSoftSkillToggle = (skill: string) => {
    setFormData(prev => {
      const currentSoftSkills = prev.soft_skills || [];
      const isSelected = currentSoftSkills.includes(skill);
      if (isSelected) {
        return {
          ...prev,
          soft_skills: currentSoftSkills.filter(s => s !== skill)
        };
      } else {
        return {
          ...prev,
          soft_skills: [...currentSoftSkills, skill]
        };
      }
    });
  };

  const handleAvailabilityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      availability: e.target.checked,
    }));
  };

  // Step rendering functions
  const renderBasicInfo = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-white mb-2">Basic Information</h3>
        <p className="text-white/70">Update your personal details</p>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-white/80 mb-2">
            Full Name
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
            required
          />
        </div>
        
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-white/80 mb-2">
            Email
          </label>
          <input
            type="email"
            id="email"
            name="email"
            value={formData.email}
            onChange={handleInputChange}
            className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
            required
          />
        </div>
        
        <div>
          <label htmlFor="phone" className="block text-sm font-medium text-white/80 mb-2">
            Phone Number
          </label>
          <input
            type="tel"
            id="phone"
            name="phone"
            value={formData.phone || ''}
            onChange={handleInputChange}
            className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
          />
        </div>

        <div>
          <label htmlFor="portfolio_url" className="block text-sm font-medium text-white/80 mb-2">
            Portfolio URL
          </label>
          <input
            type="url"
            id="portfolio_url"
            name="portfolio_url"
            value={formData.portfolio_url || ''}
            onChange={handleInputChange}
            placeholder="https://your-portfolio.com"
            className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
          />
        </div>
      </div>

      <div>
        <label htmlFor="bio" className="block text-sm font-medium text-white/80 mb-2">
          Bio
        </label>
        <textarea
          id="bio"
          name="bio"
          value={formData.bio || ''}
          onChange={handleInputChange}
          rows={4}
          placeholder="Tell us about yourself, your experience, and what you're passionate about..."
          className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
        />
      </div>

      <div className="flex items-center space-x-2">
        <input
          type="checkbox"
          id="availability"
          name="availability"
          checked={formData.availability}
          onChange={handleAvailabilityChange}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
        />
        <label htmlFor="availability" className="text-sm font-medium text-white">
          Available for new projects
        </label>
      </div>
    </div>
  );

  const renderSkillsExperience = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-white mb-2">Skills & Experience</h3>
        <p className="text-white/70">Showcase your expertise</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-white/80 mb-2">
          Skills (select up to 15)
        </label>
        
        {formData.skills && formData.skills.length > 0 && (
          <div className="mb-3">
            <div className="flex flex-wrap gap-2">
              {formData.skills.map((skill, index) => (
                <span
                  key={index}
                  className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-secondary-400/20 text-secondary-400 border border-secondary-400/30"
                >
                  <span className="truncate max-w-32">{skill}</span>
                  <button
                    type="button"
                    onClick={() => {
                      const currentSkills = formData.skills || [];
                      setFormData(prev => ({
                        ...prev,
                        skills: currentSkills.filter(s => s !== skill)
                      }));
                    }}
                    className="ml-2 text-secondary-400 hover:text-secondary-300"
                  >
                    <X size={14} />
                  </button>
                </span>
              ))}
            </div>
            <p className="text-xs text-white/50 mt-1">
              {formData.skills.length}/15 skills selected
            </p>
          </div>
        )}

        <select
          className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white focus:border-secondary-400 focus:outline-none"
          onChange={(e) => {
            if (e.target.value && !formData.skills?.includes(e.target.value)) {
              const currentSkills = formData.skills || [];
              if (currentSkills.length < 15) {
                setFormData(prev => ({
                  ...prev,
                  skills: [...currentSkills, e.target.value]
                }));
              }
            }
            e.target.value = '';
          }}
          disabled={formData.skills && formData.skills.length >= 15}
        >
          <option value="">
            {formData.skills && formData.skills.length >= 15 
              ? 'Maximum 15 skills reached' 
              : 'Select a skill to add...'
            }
          </option>
          {availableSkills
            .filter(skill => !formData.skills?.includes(skill))
            .map(skill => (
              <option key={skill} value={skill}>
                {skill}
              </option>
            ))
          }
        </select>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="experience_years" className="block text-sm font-medium text-white/80 mb-2">
            Years of Experience
          </label>
          <input
            type="number"
            id="experience_years"
            name="experience_years"
            value={formData.experience_years}
            onChange={handleNumberChange}
            min="0"
            step="0.5"
            className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
          />
        </div>
        
        <div>
          <label htmlFor="cost_per_project" className="block text-sm font-medium text-white/80 mb-2">
            Cost for Each Project ($)
          </label>
          <input
            type="number"
            id="cost_per_project"
            name="cost_per_project"
            value={formData.cost_per_project}
            onChange={handleNumberChange}
            min="0"
            step="0.01"
            className="w-full px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-white/80 mb-2">
          Languages
        </label>
        <div className="flex flex-wrap gap-2">
          {availableLanguages.map(language => (
            <button
              key={language}
              type="button"
              onClick={() => handleLanguageToggle(language)}
              className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                formData.languages?.includes(language)
                  ? 'bg-secondary-400/20 text-secondary-400 border-secondary-400/30'
                  : 'bg-white/10 text-white/70 border-white/20 hover:border-white/40'
              }`}
            >
              {language}
            </button>
          ))}
        </div>
      </div>
    </div>
  );

  const renderEducationExperience = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-white mb-2">Education & Work Experience</h3>
        <p className="text-white/70">Build your complete professional background</p>
      </div>

      {/* Education Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-white flex items-center">
            <GraduationCap size={20} className="mr-2" />
            Education
          </h4>
          <button
            type="button"
            onClick={addEducation}
            className="flex items-center px-3 py-2 bg-secondary-400/20 text-secondary-400 rounded-md hover:bg-secondary-400/30 transition-colors"
          >
            <Plus size={16} className="mr-2" />
            Add Education
          </button>
        </div>

        {(formData.education || []).map((edu, index) => (
          <div key={index} className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4">
            <div className="flex justify-between items-start mb-3">
              <h5 className="text-white font-medium">Education #{index + 1}</h5>
              <button
                type="button"
                onClick={() => removeEducation(index)}
                className="text-red-400 hover:text-red-300"
              >
                <X size={16} />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Institution"
                value={edu.institution}
                onChange={(e) => updateEducation(index, 'institution', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Degree"
                value={edu.degree}
                onChange={(e) => updateEducation(index, 'degree', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Field of Study"
                value={edu.field_of_study}
                onChange={(e) => updateEducation(index, 'field_of_study', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="date"
                value={edu.start_date}
                onChange={(e) => updateEducation(index, 'start_date', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="date"
                placeholder="End Date (leave empty if current)"
                value={edu.end_date || ''}
                onChange={(e) => updateEducation(index, 'end_date', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <textarea
                placeholder="Description"
                value={edu.description}
                onChange={(e) => updateEducation(index, 'description', e.target.value)}
                rows={3}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none sm:col-span-2"
              />
            </div>
          </div>
        ))}
      </div>

      {/* Work Experience Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-white flex items-center">
            <Building2 size={20} className="mr-2" />
            Work Experience
          </h4>
          <button
            type="button"
            onClick={addWorkExperience}
            className="flex items-center px-3 py-2 bg-secondary-400/20 text-secondary-400 rounded-md hover:bg-secondary-400/30 transition-colors"
          >
            <Plus size={16} className="mr-2" />
            Add Work Experience
          </button>
        </div>

        {(formData.work_experience || []).map((exp, index) => (
          <div key={index} className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4">
            <div className="flex justify-between items-start mb-3">
              <h5 className="text-white font-medium">Work Experience #{index + 1}</h5>
              <button
                type="button"
                onClick={() => removeWorkExperience(index)}
                className="text-red-400 hover:text-red-300"
              >
                <X size={16} />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Company"
                value={exp.company}
                onChange={(e) => updateWorkExperience(index, 'company', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Position"
                value={exp.position}
                onChange={(e) => updateWorkExperience(index, 'position', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="date"
                value={exp.start_date}
                onChange={(e) => updateWorkExperience(index, 'start_date', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="date"
                placeholder="End Date (leave empty if current)"
                value={exp.end_date || ''}
                onChange={(e) => updateWorkExperience(index, 'end_date', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <textarea
                placeholder="Description"
                value={exp.description}
                onChange={(e) => updateWorkExperience(index, 'description', e.target.value)}
                rows={3}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none sm:col-span-2"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderCertificationsReferences = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-white mb-2">Certifications & References</h3>
        <p className="text-white/70">Showcase your credentials and professional network</p>
      </div>

      {/* Certifications Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-white flex items-center">
            <Award size={20} className="mr-2" />
            Certifications
          </h4>
          <button
            type="button"
            onClick={addCertification}
            className="flex items-center px-3 py-2 bg-secondary-400/20 text-secondary-400 rounded-md hover:bg-secondary-400/30 transition-colors"
          >
            <Plus size={16} className="mr-2" />
            Add Certification
          </button>
        </div>

        {(formData.certifications || []).map((cert, index) => (
          <div key={index} className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4">
            <div className="flex justify-between items-start mb-3">
              <h5 className="text-white font-medium">Certification #{index + 1}</h5>
              <button
                type="button"
                onClick={() => removeCertification(index)}
                className="text-red-400 hover:text-red-300"
              >
                <X size={16} />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Certification Name"
                value={cert.name}
                onChange={(e) => updateCertification(index, 'name', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Issuing Organization"
                value={cert.issuing_organization}
                onChange={(e) => updateCertification(index, 'issuing_organization', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="date"
                value={cert.issue_date}
                onChange={(e) => updateCertification(index, 'issue_date', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="date"
                placeholder="Expiry Date (optional)"
                value={cert.expiry_date || ''}
                onChange={(e) => updateCertification(index, 'expiry_date', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Credential ID (optional)"
                value={cert.credential_id || ''}
                onChange={(e) => updateCertification(index, 'credential_id', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="url"
                placeholder="Credential URL (optional)"
                value={cert.credential_url || ''}
                onChange={(e) => updateCertification(index, 'credential_url', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
            </div>
          </div>
        ))}
      </div>

      {/* Social Links Section */}
      <div>
        <h4 className="text-lg font-medium text-white flex items-center mb-4">
          <LinkIcon size={20} className="mr-2" />
          Social Links
        </h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <input
            type="url"
            placeholder="LinkedIn Profile URL"
            value={formData.social_links?.linkedin || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              social_links: { ...prev.social_links, linkedin: e.target.value }
            }))}
            className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
          />
          <input
            type="url"
            placeholder="GitHub Profile URL"
            value={formData.social_links?.github || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              social_links: { ...prev.social_links, github: e.target.value }
            }))}
            className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
          />
          <input
            type="url"
            placeholder="Portfolio Website URL"
            value={formData.social_links?.website || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              social_links: { ...prev.social_links, website: e.target.value }
            }))}
            className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
          />
          <input
            type="url"
            placeholder="Twitter/X Profile URL"
            value={formData.social_links?.twitter || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              social_links: { ...prev.social_links, twitter: e.target.value }
            }))}
            className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
          />
          <input
            type="url"
            placeholder="Dribbble URL"
            value={formData.social_links?.dribbble || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              social_links: { ...prev.social_links, dribbble: e.target.value }
            }))}
            className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
          />
          <input
            type="url"
            placeholder="Behance URL"
            value={formData.social_links?.behance || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              social_links: { ...prev.social_links, behance: e.target.value }
            }))}
            className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
          />
        </div>
      </div>

      {/* References Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-white flex items-center">
            <Users size={20} className="mr-2" />
            References
          </h4>
          <button
            type="button"
            onClick={addReference}
            className="flex items-center px-3 py-2 bg-secondary-400/20 text-secondary-400 rounded-md hover:bg-secondary-400/30 transition-colors"
          >
            <Plus size={16} className="mr-2" />
            Add Reference
          </button>
        </div>

        {(formData.references || []).map((reference, index) => (
          <div key={index} className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4">
            <div className="flex justify-between items-start mb-3">
              <h5 className="text-white font-medium">Reference #{index + 1}</h5>
              <button
                type="button"
                onClick={() => removeReference(index)}
                className="text-red-400 hover:text-red-300"
              >
                <X size={16} />
              </button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input
                type="text"
                placeholder="Full Name"
                value={reference.name}
                onChange={(e) => updateReference(index, 'name', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Position"
                value={reference.position}
                onChange={(e) => updateReference(index, 'position', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Company"
                value={reference.company}
                onChange={(e) => updateReference(index, 'company', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="email"
                placeholder="Email"
                value={reference.email}
                onChange={(e) => updateReference(index, 'email', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="tel"
                placeholder="Phone (optional)"
                value={reference.phone || ''}
                onChange={(e) => updateReference(index, 'phone', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
              <input
                type="text"
                placeholder="Relationship"
                value={reference.relationship}
                onChange={(e) => updateReference(index, 'relationship', e.target.value)}
                className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderPreferencesAchievements = () => (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-white mb-2">Preferences & Achievements</h3>
        <p className="text-white/70">Define your work preferences and showcase your accomplishments</p>
      </div>

      {/* Work Preferences & Skills Section */}
      <div>
        <h4 className="text-lg font-medium text-white flex items-center mb-4">
          <Briefcase size={20} className="mr-2" />
          Work Preferences & Skills
        </h4>

        {/* Preferred Industry */}
        <div className="mb-6">
          <label className="block text-white text-sm font-medium mb-2">
            Preferred Industries
          </label>
          <div className="flex flex-wrap gap-2">
            {availableIndustries.map((industry) => (
              <button
                key={industry}
                type="button"
                onClick={() => handleIndustryToggle(industry)}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  formData.preferred_industry?.includes(industry)
                    ? 'bg-secondary-400 text-white'
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                {industry}
              </button>
            ))}
          </div>
        </div>

        {/* Tools & Technologies */}
        <div className="mb-6">
          <label className="block text-white text-sm font-medium mb-2">
            Tools & Technologies
          </label>
          <div className="flex flex-wrap gap-2">
            {availableTools.map((tool) => (
              <button
                key={tool}
                type="button"
                onClick={() => handleToolToggle(tool)}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  formData.tools_technologies?.includes(tool)
                    ? 'bg-secondary-400 text-white'
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                {tool}
              </button>
            ))}
          </div>
        </div>

        {/* Soft Skills */}
        <div className="mb-6">
          <label className="block text-white text-sm font-medium mb-2">
            Soft Skills
          </label>
          <div className="flex flex-wrap gap-2">
            {availableSoftSkills.map((skill) => (
              <button
                key={skill}
                type="button"
                onClick={() => handleSoftSkillToggle(skill)}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  formData.soft_skills?.includes(skill)
                    ? 'bg-secondary-400 text-white'
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                {skill}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Achievements Section */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-lg font-medium text-white flex items-center">
            <Award size={20} className="mr-2" />
            Achievements
          </h4>
          <button
            type="button"
            onClick={addAchievement}
            className="flex items-center px-3 py-2 bg-secondary-400/20 text-secondary-400 rounded-md hover:bg-secondary-400/30 transition-colors"
          >
            <Plus size={16} className="mr-2" />
            Add Achievement
          </button>
        </div>

        {(formData.achievements || []).map((achievement, index) => (
          <div key={index} className="bg-white/5 border border-white/10 rounded-lg p-4 mb-4">
            <div className="flex justify-between items-start mb-3">
              <h5 className="text-white font-medium">Achievement #{index + 1}</h5>
              <button
                type="button"
                onClick={() => removeAchievement(index)}
                className="text-red-400 hover:text-red-300"
              >
                <X size={16} />
              </button>
            </div>

            <textarea
              placeholder="Describe your achievement..."
              value={achievement}
              onChange={(e) => updateAchievement(index, e.target.value)}
              rows={3}
              className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none w-full"
            />
          </div>
        ))}
      </div>

      {/* Payment Preferences Section */}
      <div>
        <h4 className="text-lg font-medium text-white flex items-center mb-4">
          <CreditCard size={20} className="mr-2" />
          Payment Preferences
        </h4>

        {/* Preferred Currencies */}
        <div className="mb-6">
          <label className="block text-white text-sm font-medium mb-2">
            Preferred Currencies
          </label>
          <div className="flex flex-wrap gap-2">
            {['USD', 'EUR', 'GBP', 'CAD', 'AUD', 'JPY', 'CHF', 'CNY'].map((currency) => (
              <button
                key={currency}
                type="button"
                onClick={() => {
                  const currentCurrencies = formData.payment_preferences?.preferred_currencies || [];
                  if (currentCurrencies.includes(currency)) {
                    setFormData(prev => ({
                      ...prev,
                      payment_preferences: {
                        preferred_currencies: currentCurrencies.filter(c => c !== currency),
                        payment_methods: prev.payment_preferences?.payment_methods || ['PayPal'],
                        tax_id: prev.payment_preferences?.tax_id
                      }
                    }));
                  } else {
                    setFormData(prev => ({
                      ...prev,
                      payment_preferences: {
                        preferred_currencies: [...currentCurrencies, currency],
                        payment_methods: prev.payment_preferences?.payment_methods || ['PayPal'],
                        tax_id: prev.payment_preferences?.tax_id
                      }
                    }));
                  }
                }}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  formData.payment_preferences?.preferred_currencies?.includes(currency)
                    ? 'bg-secondary-400 text-white'
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                {currency}
              </button>
            ))}
          </div>
        </div>

        {/* Payment Methods */}
        <div className="mb-6">
          <label className="block text-white text-sm font-medium mb-2">
            Payment Methods
          </label>
          <div className="flex flex-wrap gap-2">
            {['PayPal', 'Stripe', 'Bank Transfer', 'Wise', 'Payoneer', 'Skrill'].map((method) => (
              <button
                key={method}
                type="button"
                onClick={() => {
                  const currentMethods = formData.payment_preferences?.payment_methods || [];
                  if (currentMethods.includes(method)) {
                    setFormData(prev => ({
                      ...prev,
                      payment_preferences: {
                        preferred_currencies: prev.payment_preferences?.preferred_currencies || ['USD'],
                        payment_methods: currentMethods.filter(m => m !== method),
                        tax_id: prev.payment_preferences?.tax_id
                      }
                    }));
                  } else {
                    setFormData(prev => ({
                      ...prev,
                      payment_preferences: {
                        preferred_currencies: prev.payment_preferences?.preferred_currencies || ['USD'],
                        payment_methods: [...currentMethods, method],
                        tax_id: prev.payment_preferences?.tax_id
                      }
                    }));
                  }
                }}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  formData.payment_preferences?.payment_methods?.includes(method)
                    ? 'bg-secondary-400 text-white'
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                {method}
              </button>
            ))}
          </div>
        </div>

        {/* Tax ID */}
        <div className="mb-6">
          <label className="block text-white text-sm font-medium mb-2">
            Tax ID (optional)
          </label>
          <input
            type="text"
            placeholder="Enter your tax identification number"
            value={formData.payment_preferences?.tax_id || ''}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              payment_preferences: {
                preferred_currencies: prev.payment_preferences?.preferred_currencies || ['USD'],
                payment_methods: prev.payment_preferences?.payment_methods || ['PayPal'],
                tax_id: e.target.value
              }
            }))}
            className="px-3 py-2 rounded-md bg-white/10 border border-white/20 text-white placeholder-white/50 focus:border-secondary-400 focus:outline-none"
          />
        </div>
      </div>
    </div>
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const updatedProfile = await authService.updateProfile(formData);
      updateProfile(updatedProfile);
      // Refresh profile from server to ensure consistency
      await refreshProfile();
      navigate(-1); // Go back to previous page
    } catch (error) {
      console.error('Error updating profile:', error);
      alert('Failed to update profile. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) {
    navigate('/login');
    return null;
  }

  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return renderBasicInfo();
      case 2:
        return renderSkillsExperience();
      case 3:
        return renderEducationExperience();
      case 4:
        return renderCertificationsReferences();
      case 5:
        return renderPreferencesAchievements();
      default:
        return renderBasicInfo();
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back
          </button>
          <h1 className="text-3xl font-bold text-white">Edit Profile</h1>
          <div className="w-20"></div> {/* Spacer for centering */}
        </div>

        {/* Profile Form */}
        <div className="bg-gray-800/50 backdrop-blur-sm border border-white/10 rounded-lg shadow-lg p-6">
          {/* Progress Steps */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              {Array.from({ length: totalSteps }, (_, i) => (
                <button
                  key={i + 1}
                  onClick={() => goToStep(i + 1)}
                  className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium transition-all ${
                    currentStep === i + 1
                      ? 'bg-secondary-400 text-white'
                      : currentStep > i + 1
                      ? 'bg-green-500 text-white'
                      : 'bg-white/10 text-white/50 border border-white/20'
                  }`}
                >
                  {currentStep > i + 1 ? '✓' : i + 1}
                </button>
              ))}
            </div>
            <div className="flex justify-between text-xs text-white/60">
              <span>Basic Info</span>
              <span>Skills</span>
              <span>Education & Work</span>
              <span>Certifications</span>
              <span>Preferences</span>
            </div>
          </div>

          {/* Step Content */}
          <form onSubmit={handleSubmit}>
            {renderStepContent()}

            {/* Navigation Buttons */}
            <div className="flex justify-between mt-8">
              <button
                type="button"
                onClick={prevStep}
                disabled={currentStep === 1}
                className="px-6 py-2 border border-white/20 rounded-md text-white/70 hover:text-white hover:border-white/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>

              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={nextStep}
                  className="px-6 py-2 bg-secondary-400 text-white rounded-md hover:bg-secondary-500 transition-colors"
                >
                  Next
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={isLoading}
                  className="flex items-center px-6 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} className="mr-2" />
                      Save Profile
                    </>
                  )}
                </button>
              )}
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditProfile;
